import React, { memo, useMemo } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';

import { BasketActionsType, BasketItemType } from '../../../types/basket';
import * as Api from '../../../weborder/Api';
import * as BasketControl from '../../../weborder/BasketControl';
import { Button, ShoppingCartCounter, Typography } from '../../atoms';
import { InfoFilled as InfoFilledIcon } from '../../icons';
import { BasketItem, GroupHeader } from '../../molecules';
import { UnavailableBasketItem } from './components/UnavailableBasketItem';
import { basketUtils } from './utils/basketUtils';

export interface BasketProps {
  actions: BasketActionsType;
  basketItemClassName?: string;
  basketItems: BasketControl.Item[];
  buttonLabel: string;
  className?: string;
  currency: Api.Currency;
  isCheckoutEnabled: boolean;
  languageCode: string;
  menu: Api.MenuReposnse;
  minOrder: number;
  showBasketTitle?: boolean;
  showCheckoutButton?: boolean;
  translate: any;
  unavailableBasketItems?: string[];
}

const useStyles = makeStyles((theme: Theme) => ({
  basket: {
    backgroundColor: theme.palette.common.white,
    zIndex: theme.zIndex.mobileStepper,

    '& $basketItems': {
      display: 'none',
    },

    '& $header': {
      display: 'none',
    },
  },

  header: {
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    display: 'flex',
    flexFlow: 'row nowrap',
    margin: theme.spacing(0, 3),
    padding: theme.spacing(3, 0, 2.5),

    '& .shoppingCart': {
      marginRight: theme.spacing(2.5),
    },
  },

  basketItems: {
    margin: theme.spacing(0, 0, 0, 3),
    paddingRight: theme.spacing(3),
    '& ul': {
      paddingLeft: 0,
    },
  },

  checkoutButton: {
    '& .buttonState': {
      alignItems: 'center',
      display: 'flex',
      flexFlow: 'row nowrap',
      width: '100%',
    },

    '& [class*="MuiButton-contained"]': {
      padding: theme.spacing(2.5, 2),
    },
  },

  buttonContent: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    width: '100%',
  },
  minOrderWarning: {
    alignItems: 'center',
    color: theme.palette.grey[800],
    display: 'flex',
    flexFlow: 'row nowrap',

    '& svg': {
      color: theme.palette.info.main,
      marginRight: theme.spacing(1),
    },
  },

  [theme.breakpoints.up('lg')]: {
    basket: {
      display: 'flex',
      flexDirection: 'column',
      width: theme.spacing(47.25),

      '& $basketItems': {
        display: 'block',
      },

      '& $header': {
        display: 'flex',
        flexFlow: 'row nowrap',
      },

      '& .concessionBrand:first-of-type': {
        marginTop: theme.spacing(2),
      },
    },

    basketItems: {
      paddingLeft: 0,
      paddingBottom: theme.spacing(1.5),
    },

    concessionBrand: {
      '& .basketItems': {
        marginLeft: 0,
        padding: 0,
        '& li': {
          borderBottom: `1px solid ${theme.palette.grey[400]}`,
          '&:last-of-type': {
            borderBottom: 'none',
          },
        },
      },
    },
  },
}));

function BasketComponent(props: BasketProps): JSX.Element {
  const classes = useStyles();
  const {
    actions,
    basketItemClassName,
    basketItems,
    buttonLabel,
    className,
    currency,
    isCheckoutEnabled,
    languageCode,
    menu,
    minOrder,
    showBasketTitle = true,
    showCheckoutButton = true,
    translate,
    unavailableBasketItems = [],
  } = props;

  const { ConcessionStores: concessionStores, MenuId: menuId } = menu;
  const hasConcessionStores = menu?.ConcessionStores.length > 0;

  const basketTotal = BasketControl.Total(basketItems);
  const basketItemsSummary = basketUtils.summariseBasketItems(basketItems);

  const isOrderSufficient = basketTotal >= minOrder;
  const localisedBasketTotal = BasketControl.ToCurrency(
    basketTotal,
    currency,
    languageCode,
  );
  const localisedMinOrder = BasketControl.ToCurrency(
    minOrder,
    currency,
    languageCode,
  );

  const renderCheckoutButton = (): JSX.Element => {
    let buttonContent = null;

    if (isOrderSufficient) {
      buttonContent = (
        <Typography variant="h3">
          <em>{buttonLabel}</em>
        </Typography>
      );
    } else {
      buttonContent = (
        <div className={classes.minOrderWarning}>
          <InfoFilledIcon size="xs" />
          <Typography variant="body2">
            {translate('Menu_Basket_MinimumOrder')} {localisedMinOrder}
          </Typography>
        </div>
      );
    }

    const isEnabled = isOrderSufficient && isCheckoutEnabled;

    return (
      <Button disabled={!isEnabled} fullWidth onClick={actions.onCheckout}>
        <div className={classes.buttonContent}>
          {buttonContent}

          <Typography variant="h3">
            <em>{localisedBasketTotal}</em>
          </Typography>
        </div>
      </Button>
    );
  };

  const basketItemCount = Object.values(basketItemsSummary).reduce(
    (acc, basketItem) => {
      // eslint-disable-next-line no-param-reassign
      acc += basketItem.quantity;
      return acc;
    },
    0,
  );

  const basketStyles = clsx(classes.basket, 'fd-basket', className || '');
  const basketItemStyles = clsx(classes.basketItems, basketItemClassName || '');
  const concessionBrandStyles = clsx(
    classes.concessionBrand,
    'concessionBrand',
  );

  const renderBasketItems = (data): JSX.Element => (
    <ul className={basketItemStyles}>
      {Object.values(data).map((basketItem: BasketItemType | any) => {
        const itemData = basketItem?.itemData
          ? basketItem.itemData
          : basketItem;
        const key = basketUtils.getBasketItemKey(itemData);
        return (
          <BasketItem
            actions={actions}
            basketItem={itemData}
            currency={currency}
            languageCode={languageCode}
            key={key}
            menuId={menuId}
            translate={translate}
          />
        );
      })}
      {unavailableBasketItems.length > 0 && renderUnavailableBasketItems()}
    </ul>
  );

  const renderUnavailableBasketItems = (): JSX.Element[] =>
    unavailableBasketItems.map((unavailableBasketItem) => (
      <UnavailableBasketItem
        key={unavailableBasketItem}
        name={unavailableBasketItem}
        translate={translate}
      />
    ));

  const renderConcessionBrand = (id, concessionStoresArray): JSX.Element => {
    const concessionBrand = basketUtils.getConcessionBrand(
      id,
      concessionStoresArray,
    );
    return (
      <GroupHeader
        src={concessionBrand?.ImageUrl}
        title={concessionBrand?.Name}
      />
    );
  };

  const renderConcessionBasketItems = (): JSX.Element => {
    const concessionBasketItems =
      basketUtils.summariseConcessionBasketItems(basketItemsSummary);
    const concessionStoresArray = hasConcessionStores
      ? Object.values(concessionStores)
      : [];

    return (
      <ul className={basketItemStyles}>
        {Object.entries(concessionBasketItems).map(
          ([concessionStoreId, items]) => (
            <li className={concessionBrandStyles} key={concessionStoreId}>
              {renderConcessionBrand(
                Number(concessionStoreId),
                concessionStoresArray,
              )}
              {renderBasketItems(Object.values(items))}
            </li>
          ),
        )}
      </ul>
    );
  };

  const renderBasketContents = useMemo(
    () =>
      hasConcessionStores
        ? renderConcessionBasketItems()
        : renderBasketItems(basketItemsSummary),
    [basketItems],
  );

  return (
    <div className={basketStyles} data-fd="menu-basket">
      <div className="basketOutline">
        {showBasketTitle ? (
          <div className={classes.header}>
            <ShoppingCartCounter
              className="shoppingCart"
              count={basketItemCount}
              size="s"
            />
            <Typography variant="h3">{translate('Basket')}</Typography>
          </div>
        ) : null}

        {renderBasketContents}
      </div>

      {showCheckoutButton && (
        <div className={classes.checkoutButton} data-fd="checkout-button">
          {renderCheckoutButton()}
        </div>
      )}
    </div>
  );
}

const Basket = memo(BasketComponent);
Basket.displayName = 'Basket';

export { Basket };
